import { useRef, useCallback, useLayoutEffect, useEffect, useState, useMemo, createRef } from 'react';
import { gsap } from 'gsap';
import SplitText from 'gsap/dist/SplitText';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __spreadArray(to, from) {
    for (var i = 0, il = from.length, j = to.length; i < il; i++, j++)
        to[j] = from[i];
    return to;
}

// Based on @aholachek ;)
// https://twitter.com/chpwn/status/285540192096497664
// https://medium.com/@nathangitter/building-fluid-interfaces-ios-swift-9732bb934bf5
function rubberband(distance, dimension, constant) {
    if (dimension === 0 || Math.abs(dimension) === Infinity) {
        return Math.pow(distance, constant * 5);
    }
    return (distance * dimension * constant) / (dimension + constant * distance);
}
// iOS constant = 0.55
function rubberbandIfOutOfBounds(position, min, max, constant) {
    if (constant === void 0) { constant = 0.55; }
    if (position < min) {
        return -rubberband(min - position, max - min, constant) + min;
    }
    if (position > max) {
        return +rubberband(position - max, max - min, constant) + max;
    }
    return position;
}
function calculateDistance(movement) {
    return Math.hypot.apply(Math, movement);
}
function calculateAllKinematics(movement, delta, dt) {
    var dl = calculateDistance(delta);
    var alpha = dl === 0 ? 0 : 1 / dl;
    var beta = dt === 0 ? 0 : 1 / dt;
    var velocity = beta * dl;
    var velocities = delta.map(function (v) { return beta * v; });
    var direction = delta.map(function (v) { return alpha * v; });
    var distance = calculateDistance(movement);
    return { velocities: velocities, velocity: velocity, distance: distance, direction: direction };
}

function clamp(number, min, max) {
    return Math.min(Math.max(number, min), max);
}

/**
 * Returns the latest state, get access to the latest value of some props or state inside an asynchronous callback
 * @param value latest value of some props or state to record
 *
 * @example
 * ```ts
 * import { useState } from 'react'
 * import { useLatest } from '@unlikelystudio/react-hooks'
 *
 * const Demo = () => {
 *   const [state, setState] = useState()
 *   const latestState = useLatest(state)
 *
 *   return null
 * }
 * ```
 */
function useLatest(value) {
    var ref = useRef(value);
    ref.current = value;
    return ref;
}

/**
 * Returns a stable function that delegates to the latest version of the passed argument
 * @param value latest function to memoize
 *
 * @example
 * ```ts
 * import { useLatestCallback } from '@unlikelystudio/react-hooks'
 *
 * const Demo = ({ callback }) => {
 *   const latestCallback = useLatestCallback(callback)
 *
 *   return null
 * }
 * ```
 */
function useLatestCallback(value) {
    var latestValue = useLatest(value);
    return useCallback(function (params) {
        var _a;
        return (_a = latestValue.current) === null || _a === void 0 ? void 0 : _a.call(latestValue, params);
    }, []);
}

var canUseDOM = !!(typeof window !== 'undefined' &&
    typeof window.document !== 'undefined' &&
    typeof window.document.createElement !== 'undefined');

/**
 * useLayoutEffect that does not show warning when server-side rendering
 * @see {@link https://medium.com/@alexandereardon/uselayouteffect-and-ssr-192986cdcf7a|Alex Reardon's article}
 * @param effect imperative function that can return a cleanup function
 * @param deps if present, effect will only activate if the values in the list change.
 * @example
 * ```tsx
 * import { useIsomorphicLayoutEffect } from '@unlikelystudio/react-hooks'
 *
 * const Demo = ({ value }) => {
 *   useIsomorphicLayoutEffect(() => {
 *     window.console.log(value)
 *   }, [value])
 *
 *   return null
 * }
 * ```
 */
var useIsomorphicLayoutEffect = canUseDOM ? useLayoutEffect : useEffect;

function getTap(_a) {
    var mx = _a[0], my = _a[1];
    return [Math.abs(mx) < 3, Math.abs(my) < 3];
}
function isTouchEvent(e) {
    var _a, _b;
    return (_b = ((_a = e === null || e === void 0 ? void 0 : e.touches) === null || _a === void 0 ? void 0 : _a.length) > 0) !== null && _b !== void 0 ? _b : false;
}
function getXyFromEvent(e) {
    var xy;
    var touchEvent = isTouchEvent(e);
    if (touchEvent) {
        xy = [e.touches[0].clientX, e.touches[0].clientY];
    }
    else {
        xy = [e === null || e === void 0 ? void 0 : e.clientX, e === null || e === void 0 ? void 0 : e.clientY];
    }
    return xy;
}
function useDrag(_a) {
    var ref = _a.ref, snap = _a.snap, onDrag = _a.onDrag, onDragStartCallback = _a.onDragStart, onDragEndCallback = _a.onDragEnd, onSnap = _a.onSnap, _b = _a.throwFactor, throwFactor = _b === void 0 ? [0, 0] : _b, _c = _a.bounds, bounds = _c === void 0 ? {
        top: -Infinity,
        left: -Infinity,
        right: Infinity,
        bottom: Infinity,
    } : _c, _d = _a.axis, axis = _d === void 0 ? 'free' : _d, _e = _a.rubberband, rubberband = _e === void 0 ? 0 : _e, _f = _a.enable, enable = _f === void 0 ? true : _f, _g = _a.preventUnintentionaClick, preventUnintentionaClick = _g === void 0 ? true : _g;
    var axisIndex = axis === 'free' ? null : axis === 'x' ? 0 : 1;
    var opposedAxisIndex = axis === 'free' ? null : axis === 'x' ? 1 : 0;
    var latestOnDrag = useLatestCallback(onDrag);
    var dragTarget = ref !== null && ref !== void 0 ? ref : useRef();
    var dragState = useRef({
        down: false,
        kinematics: {
            velocities: [0, 0],
            velocity: 0,
            direction: [0, 0],
            distance: 0,
        },
        tap: [true, true],
        timeStamp: 0,
        xy: [0, 0],
        delta: [0, 0],
        offset: [0, 0],
        offsetWithRubberband: [0, 0],
        movement: [0, 0],
        scrolling: false,
        active: false,
        touches: false,
    });
    var setDragState = useLatestCallback(function (newDragState) {
        var _a, _b, _c, _d;
        if (axis === 'x' &&
            ((_a = newDragState === null || newDragState === void 0 ? void 0 : newDragState.offset) === null || _a === void 0 ? void 0 : _a[1]) &&
            ((_b = newDragState === null || newDragState === void 0 ? void 0 : newDragState.offsetWithRubberband) === null || _b === void 0 ? void 0 : _b[1])) {
            newDragState.offset[1] = dragState.current.offset[1];
            newDragState.offsetWithRubberband[1] =
                dragState.current.offsetWithRubberband[1];
        }
        else if (axis === 'y' &&
            ((_c = newDragState === null || newDragState === void 0 ? void 0 : newDragState.offset) === null || _c === void 0 ? void 0 : _c[0]) &&
            ((_d = newDragState === null || newDragState === void 0 ? void 0 : newDragState.offsetWithRubberband) === null || _d === void 0 ? void 0 : _d[0])) {
            newDragState.offset[0] = dragState.current.offset[0];
            newDragState.offsetWithRubberband[0] =
                dragState.current.offsetWithRubberband[0];
        }
        dragState.current = __assign(__assign({}, dragState.current), newDragState);
    });
    var addEvents = function () {
        document.addEventListener('touchmove', onDragChange, {
            passive: false,
        });
        document.addEventListener('touchcancel', onDragEnd);
        document.addEventListener('touchend', onDragEnd);
        document.addEventListener('mousemove', onDragChange, {
            passive: false,
        });
        document.addEventListener('mouseup', onDragEnd);
        document.addEventListener('mouseleave', onDragEnd);
    };
    var removeEvents = function () {
        document.removeEventListener('touchmove', onDragChange);
        document.removeEventListener('touchcancel', onDragEnd);
        document.removeEventListener('touchend', onDragEnd);
        document.removeEventListener('mousemove', onDragChange);
        document.removeEventListener('mouseup', onDragEnd);
        document.removeEventListener('mouseleave', onDragEnd);
    };
    // On Click Capture
    useIsomorphicLayoutEffect(function () {
        var _a;
        var onClickCapture = function (e) {
            var _a = dragState.current.tap, tapX = _a[0], tapY = _a[1];
            if (!tapX || !tapY) {
                e.preventDefault();
                e.stopPropagation();
                return false;
            }
        };
        if (preventUnintentionaClick) {
            (_a = dragTarget === null || dragTarget === void 0 ? void 0 : dragTarget.current) === null || _a === void 0 ? void 0 : _a.addEventListener('click', onClickCapture, true);
        }
        return function () {
            var _a;
            (_a = dragTarget === null || dragTarget === void 0 ? void 0 : dragTarget.current) === null || _a === void 0 ? void 0 : _a.removeEventListener('click', onClickCapture, true);
        };
    }, [preventUnintentionaClick]);
    // On Drag Start
    useIsomorphicLayoutEffect(function () {
        var _a, _b;
        var onDragStart = function (e) {
            addEvents();
            setDragState({
                timeStamp: e.timeStamp,
                tap: [true, true],
                down: true,
                movement: [0, 0],
                xy: getXyFromEvent(e),
                touches: isTouchEvent(e),
            });
            latestOnDrag === null || latestOnDrag === void 0 ? void 0 : latestOnDrag(dragState.current);
            onDragStartCallback === null || onDragStartCallback === void 0 ? void 0 : onDragStartCallback(dragState.current);
        };
        if (enable) {
            (_a = dragTarget === null || dragTarget === void 0 ? void 0 : dragTarget.current) === null || _a === void 0 ? void 0 : _a.addEventListener('touchstart', onDragStart, {
                passive: false,
            });
            (_b = dragTarget === null || dragTarget === void 0 ? void 0 : dragTarget.current) === null || _b === void 0 ? void 0 : _b.addEventListener('mousedown', onDragStart, {
                passive: false,
            });
        }
        return function () {
            var _a, _b;
            removeEvents();
            (_a = dragTarget === null || dragTarget === void 0 ? void 0 : dragTarget.current) === null || _a === void 0 ? void 0 : _a.removeEventListener('touchstart', onDragStart);
            (_b = dragTarget === null || dragTarget === void 0 ? void 0 : dragTarget.current) === null || _b === void 0 ? void 0 : _b.removeEventListener('mousedown', onDragStart);
        };
    }, [enable]);
    var onDragChange = useLatestCallback(function (e) {
        if (!dragState.current.down)
            return;
        var xy = getXyFromEvent(e);
        var prevTimeStamp = dragState.current.timeStamp;
        var timeStamp = e.timeStamp;
        var x = xy[0], y = xy[1];
        var _a = dragState.current.xy, prevX = _a[0], prevY = _a[1];
        var _b = dragState.current.movement, prevMvX = _b[0], prevMvY = _b[1];
        var _c = dragState.current.offset, prevOffsetX = _c[0], prevOffsetY = _c[1];
        var _d = [x - prevX, y - prevY], deltaX = _d[0], deltaY = _d[1];
        var movement = [prevMvX + deltaX, prevMvY + deltaY];
        var offset = [prevOffsetX + deltaX, prevOffsetY + deltaY];
        var oX = offset[0], oY = offset[1];
        var top = bounds.top, bottom = bounds.bottom, left = bounds.left, right = bounds.right;
        var offsetWithRubberband = [
            rubberbandIfOutOfBounds(oX, left, right, rubberband),
            rubberbandIfOutOfBounds(oY, top, bottom, rubberband),
        ];
        var kinematics = calculateAllKinematics(movement, [deltaX, deltaY], timeStamp - prevTimeStamp);
        var tap = getTap(movement);
        var active = !dragState.current.active
            ? axis !== 'free'
                ? !tap[axisIndex] && !dragState.current.scrolling
                : !tap[axisIndex] && !tap[opposedAxisIndex]
            : dragState.current.active;
        var scrolling = axis !== 'free' ? Math.abs(movement[opposedAxisIndex]) > 3 : false;
        if (e.cancelable && active) {
            e.preventDefault();
        }
        setDragState(__assign({ xy: xy,
            kinematics: kinematics,
            timeStamp: timeStamp,
            movement: movement,
            active: active,
            scrolling: scrolling,
            tap: tap, delta: [deltaX, deltaY], touches: isTouchEvent(e) }, (active ? { offset: offset, offsetWithRubberband: offsetWithRubberband } : {})));
        latestOnDrag === null || latestOnDrag === void 0 ? void 0 : latestOnDrag(dragState.current);
    });
    var onDragEnd = useLatestCallback(function (e) {
        removeEvents();
        var active = dragState.current.active;
        var _a = dragState.current.offset, oX = _a[0], oY = _a[1];
        var top = bounds.top, bottom = bounds.bottom, left = bounds.left, right = bounds.right;
        var _b = [
            dragState.current.kinematics.velocities[0] * throwFactor[0],
            dragState.current.kinematics.velocities[1] * throwFactor[1],
        ], throwX = _b[0], throwY = _b[1];
        var offset = [oX + throwX, oY + throwY];
        // Snap offset
        offset = snap ? snap(offset) : offset;
        // Clamp offset
        offset = [clamp(offset[0], left, right), clamp(offset[1], top, bottom)];
        setDragState(__assign(__assign({ timeStamp: e.timeStamp }, (active ? { offset: offset, offsetWithRubberband: offset } : {})), { down: false, scrolling: false, active: false, touches: isTouchEvent(e) }));
        latestOnDrag === null || latestOnDrag === void 0 ? void 0 : latestOnDrag(dragState.current);
        onDragEndCallback === null || onDragEndCallback === void 0 ? void 0 : onDragEndCallback(dragState.current);
        snap && (onSnap === null || onSnap === void 0 ? void 0 : onSnap(dragState.current));
    });
    useIsomorphicLayoutEffect(function () {
        return function () {
            removeEvents();
        };
    }, []);
    var setOffset = function (offset) {
        var oX = offset[0], oY = offset[1];
        var _a = dragState.current.offset, currentOX = _a[0], currentOY = _a[1];
        var _b = [oX - currentOX, oY - currentOY], deltaX = _b[0], deltaY = _b[1];
        var top = bounds.top, bottom = bounds.bottom, left = bounds.left, right = bounds.right;
        var _c = dragState.current.movement, prevMvX = _c[0], prevMvY = _c[1];
        var movement = [prevMvX + deltaX, prevMvY + deltaY];
        var prevTimeStamp = dragState.current.timeStamp;
        var timeStamp = Date.now();
        var kinematics = calculateAllKinematics(movement, [deltaX, deltaY], timeStamp - prevTimeStamp);
        var newOffset = [clamp(oX, left, right), clamp(oY, top, bottom)];
        setDragState({
            kinematics: kinematics,
            offset: newOffset,
            offsetWithRubberband: newOffset,
        });
        onDrag === null || onDrag === void 0 ? void 0 : onDrag(dragState.current);
    };
    var addOffset = function (offset) {
        var _a = dragState.current.offset, currentOX = _a[0], currentOY = _a[1];
        var deltaOX = offset[0], deltaOY = offset[1];
        setOffset([currentOX + deltaOX, currentOY + deltaOY]);
    };
    return {
        addOffset: addOffset,
        setOffset: setOffset,
        dragState: dragState,
    };
}

/**
 * Sensor hooks that track if some element is being hovered by a mouse
 * @param params onMouseEnter and onMouseLeave callbacks
 * @param params.onMouseEnter callback
 * @param params.onMouseLeave callback
 * @example
 * ```tsx
 * import { useIsHover } from '@unlikelystudio/react-hooks'
 *
 * const Demo = () => {
 *   const [isHover, callbacks] = useIsHover({
 *     onMouseEnter: () => console.log('mouse enter'),
 *     onMouseLeave: () => console.log('mouse leave')
 *   })
 *
 *   return (
 *     <div {...callbacks}>
 *       {isHover ? 'HOVERED' : ''}
 *     </div>
 *   )
 * }
 * ```
 */
function useIsHover(_a) {
    var _b = _a === void 0 ? {} : _a, onMouseEnter = _b.onMouseEnter, onMouseLeave = _b.onMouseLeave;
    var _c = useState(false), isHover = _c[0], setIsHover = _c[1];
    var customOnMouseEnter = useCallback(function (e) {
        onMouseEnter && onMouseEnter(e);
        setIsHover(true);
    }, [onMouseEnter]);
    var customOnMouseLeave = useCallback(function (e) {
        onMouseLeave && onMouseLeave(e);
        setIsHover(false);
    }, [onMouseLeave]);
    return [
        isHover,
        {
            onMouseEnter: customOnMouseEnter,
            onMouseLeave: customOnMouseLeave,
        },
    ];
}

/**
 * Lifecycle hook providing ability to check component's mount state
 *
 * @example
 * ```ts
 * import { useIsMounted } from '@unlikelystudio/react-hooks'

 * const Demo = () => {
 *   const isMounted = useIsMounted()

 *   React.useEffect(() => {
 *     console.log(isMounted.current)
 *   }, [isMounted])
 * }
 * ```
 */
function useIsMounted() {
    var isMountedRef = useRef(false);
    useIsomorphicLayoutEffect(function () {
        isMountedRef.current = true;
        return function () {
            isMountedRef.current = false;
        };
    }, []);
    return isMountedRef;
}

/**
 * Lifecycle hook providing ability to check component's mount state
 *
 * **This hook cause component re-render**
 *
 * @example
 * ```ts
 * import { useIsMountedState } from '@unlikelystudio/react-hooks'

 * const Demo = () => {
 *   const isMounted = useIsMountedState()

 *   React.useEffect(() => {
 *     console.log(isMounted)
 *   }, [isMounted])
 * }
 * ```
 */
function useIsMountedState() {
    var _a = useState(false), isMounted = _a[0], setIsMounted = _a[1];
    useIsomorphicLayoutEffect(function () {
        setIsMounted(true);
    }, []);
    return isMounted;
}

var hasTouchScreen = !!(typeof window !== 'undefined' &&
    ('ontouchstart' in window ||
        (window.DocumentTouch &&
            typeof document !== 'undefined' &&
            document instanceof window.DocumentTouch))) ||
    !!(typeof navigator !== 'undefined' &&
        (navigator.maxTouchPoints || navigator.msMaxTouchPoints));
/**
 * Returns touch events availability on devices
 *
 * **This hook does not track touch availability changes**
 *
 * @example
 * ```ts
 * import { useIsTouchScreen } from '@unlikelystudio/react-hooks'
 *
 * const Demo = () => {
 *   const isTouchable = useIsTouchScreen()
 *   console.log(isTouchable)
 *
 *   return null
 * }
 * ```
 */
function useIsTouchScreen() {
    return hasTouchScreen;
}

let createNanoEvents = () => ({
  events: {},
  emit(event, ...args) {
(this.events[event] || []).forEach(i => i(...args));
  },
  on(event, cb) {
(this.events[event] = this.events[event] || []).push(cb);
    return () =>
      (this.events[event] = (this.events[event] || []).filter(i => i !== cb))
  }
});

var emitter$1 = createNanoEvents();
if (canUseDOM) {
    window.addEventListener('resize', function () {
        window.requestAnimationFrame(function () {
            emitter$1.emit('resize', {
                width: window.innerWidth,
                height: window.innerHeight,
            });
        });
    }, { passive: true });
}
var width = canUseDOM && window.innerWidth;
var height = canUseDOM && window.innerHeight;
/**
 * Callback hook triggered on window size update
 * @param callback function to call on window size update
 *
 * @example
 * ```ts
 * import { useOnResize } from '@unlikelystudio/react-hooks'
 *
 * const Demo = () => {
 *   const windowSize = useOnResize((windowSize) => {
 *     console.log('RESIZE', windowSize)
 *   })
 *
 *   return null
 * }
 * ```
 */
function useOnResize(callback) {
    var isMounted = useIsMounted();
    var onResize = useLatestCallback(function (windowSize) {
        if (isMounted.current) {
            callback(windowSize);
        }
    });
    useIsomorphicLayoutEffect(function () {
        var unbind = emitter$1.on('resize', onResize);
        onResize({ width: width, height: height });
        return function () {
            unbind();
        };
    }, []);
}

/**
 * Tracks an HTML element's dimensions
 * @param ref sized element
 *
 * @example
 * ```ts
 * import { useRef } from 'react
 * import { useMeasure } from '@unlikelystudio/react-hooks'
 *
 * const Demo = () => {
 *   const componentRef = useRef()
 *   const rect = useMeasure(componentRef)
 *
 *   console.log(rect)
 *
 *   return <div ref={componentRef} />
 * }
 * ```
 */
function useMeasure(ref) {
    var _a = useState({
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
    }), rect = _a[0], setRect = _a[1];
    useOnResize(function () {
        if (ref.current) {
            var rect_1 = ref.current.getBoundingClientRect();
            setRect({
                width: rect_1.width,
                height: rect_1.height,
                top: rect_1.top,
                left: rect_1.left,
                right: rect_1.right,
                bottom: rect_1.bottom,
                x: rect_1.x,
                y: rect_1.y,
            });
        }
    });
    return rect;
}

/**
 * Updates when the size of the window changes
 * @param defaultWindowSize
 *
 * @example
 * ```ts
 * import { useWindowSize } from '@unlikelystudio/react-hooks'
 *
 * const Demo = () => {
 *   const windowSize = useWindowSize({ width: 1920, height: 1080 })
 *   console.log(windowSize)
 *
 *   return null
 * }
 * ```
 */
function useWindowSize(defaultWindowSize) {
    var _a = useState(__assign({ width: 0, height: 0 }, defaultWindowSize)), windowSize = _a[0], setWindowSize = _a[1];
    useOnResize(function (windowSize) {
        setWindowSize(windowSize);
    });
    return windowSize;
}

var emitter = createNanoEvents();
var observer = canUseDOM &&
    new window.ResizeObserver(function (entries) {
        emitter.emit('resize', entries);
    });
/**
 * Sensor that tracks dimensions of an HTML element using the Resize Observer API
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver|ResizeObserver}
 * @param ref HTML object to listen
 * @param method method used to compute Rect
 *
 * @example
 * ```ts
 * import { useRef } from 'react
 * import { useMeasureObserver } from '@unlikelystudio/react-hooks'
 *
 * const Demo = () => {
 *   const componentRef = useRef()
 *   const rect = useMeasureObserver(componentRef)
 *
 *   console.log(rect)
 *
 *   return <div ref={componentRef} />
 * }
 * ```
 */
function useMeasureObserver(ref, method) {
    var isMounted = useIsMounted();
    var _a = useState(method === 'borderBoxSize'
        ? {
            width: 0,
            height: 0,
        }
        : {
            width: 0,
            height: 0,
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            x: 0,
            y: 0,
        }), rect = _a[0], setRect = _a[1];
    useIsomorphicLayoutEffect(function () {
        var unbind;
        if (ref === null || ref === void 0 ? void 0 : ref.current) {
            observer.observe(ref === null || ref === void 0 ? void 0 : ref.current, { box: 'border-box' });
            unbind = emitter.on('resize', function (entries) {
                if (!isMounted.current)
                    return;
                entries.forEach(function (entry) {
                    var _a, _b, _c, _d;
                    if (entry.target === ref.current) {
                        var borderBoxHeight = (_b = (_a = entry === null || entry === void 0 ? void 0 : entry.borderBoxSize) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.blockSize;
                        var borderBoxWidth = (_d = (_c = entry === null || entry === void 0 ? void 0 : entry.borderBoxSize) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.inlineSize;
                        var rect_1;
                        if (method === 'getBoundingClientRect' ||
                            !borderBoxHeight ||
                            !borderBoxWidth) {
                            rect_1 = entry.target.getBoundingClientRect();
                        }
                        setRect(__assign({ width: borderBoxWidth !== null && borderBoxWidth !== void 0 ? borderBoxWidth : rect_1.width, height: borderBoxHeight !== null && borderBoxHeight !== void 0 ? borderBoxHeight : rect_1.height }, (method === 'getBoundingClientRect'
                            ? {
                                bottom: rect_1.bottom,
                                top: rect_1.top,
                                left: rect_1.left,
                                right: rect_1.right,
                                x: rect_1.x,
                                y: rect_1.y,
                            }
                            : {})));
                    }
                });
            });
        }
        return function () {
            unbind === null || unbind === void 0 ? void 0 : unbind();
            if (ref === null || ref === void 0 ? void 0 : ref.current) {
                observer.unobserve(ref === null || ref === void 0 ? void 0 : ref.current);
            }
        };
    }, [ref === null || ref === void 0 ? void 0 : ref.current]);
    var windowSize = useWindowSize();
    useIsomorphicLayoutEffect(function () {
        if ((ref === null || ref === void 0 ? void 0 : ref.current) && method === 'getBoundingClientRect') {
            var newRect = ref.current.getBoundingClientRect();
            setRect({
                width: newRect.width,
                height: newRect.height,
                bottom: newRect.bottom,
                top: newRect.top,
                left: newRect.left,
                right: newRect.right,
                x: newRect.x,
                y: newRect.y,
            });
        }
    }, [windowSize]);
    return rect;
}

/**
 * Call given function within the RAF loop without re-rendering parent component
 * @param callback function to call each RAF tick
 * @param autoPlay whether loop should be started at initial render
 *
 * @example
 * ```ts
 * import { useRaf } from '@unlikelystudio/react-hooks'
 *
 * const Demo = () => {
 *   const [loopStop, loopStart, isActive] = useRaf((time) => {
 *     console.log('UPDATE', time)
 *   })
 *
 *   return null
 * }
 * ```
 */
function useRaf(callback, autoPlay) {
    if (autoPlay === void 0) { autoPlay = true; }
    var raf = useRef(null);
    var rafActivity = useRef(false);
    var rafCallback = useRef(callback);
    rafCallback.current = callback;
    var step = useCallback(function (time) {
        if (rafActivity.current) {
            rafCallback.current(time);
            raf.current = requestAnimationFrame(step);
        }
    }, []);
    var result = useMemo(function () { return [
        function () {
            if (rafActivity.current) {
                rafActivity.current = false;
                raf.current && cancelAnimationFrame(raf.current);
            }
        },
        function () {
            if (!rafActivity.current) {
                rafActivity.current = true;
                raf.current = requestAnimationFrame(step);
            }
        },
        function () { return rafActivity.current; },
    ]; }, []);
    useEffect(function () {
        if (autoPlay) {
            result[1]();
        }
        return result[0];
    }, []);
    return result;
}

function generateRefs(length) {
    return __spreadArray([], Array(length)).map(function () { return createRef(); });
}
/**
 * Generate multiple refs
 * @param length desired refs generated
 *
 * @example
 * ```ts
 * import { useRefs } from '@unlikelystudio/react-hooks'
 *
 * const Demo = ({ items }) => {
 *   const itemsRefs = useRefs(items.length)
 *
 *   return items.map((item, i) => {
 *     <div key={i} ref={itemsRefs.current[i]}>{item}</div>
 *   })
 * }
 * ```
 */
function useRefs(length) {
    var prevLength = useRef(length);
    var refs = useRef(generateRefs(length));
    if (length !== prevLength.current) {
        if (length > prevLength.current) {
            refs.current = __spreadArray(__spreadArray([], refs.current), generateRefs(length - prevLength.current));
        }
        else if (length < prevLength.current) {
            if (length === 0) {
                refs.current = [];
            }
            else {
                refs.current = __spreadArray([], refs.current.slice(0, length));
            }
        }
        prevLength.current = length;
    }
    return refs;
}

/**
 * Get a function to set styles on multiple elements using gsap.quickSetter
 * @param refs HTMLElement targets, React reference of multiple references
 * @param setStyleParams gsap quickSetter params
 * @param deps if present, quickSetter will rebuild if the values in the list change
 *
 * @example
 * ```tsx
 * import { useIsomorphicLayoutEffect, useRefs } from '@unlikelystudio/react-hooks'
 * import { useSetStyles } from '@unlikelystudio/react-gsap-hooks'
 *
 * const Demo = ({ items }) => {
 *   const itemsRefs = useRefs<HTMLDivElement>(items.length)
 *   const setStyles = useSetStyles(itemsRefs)
 *
 *   useIsomorphicLayoutEffect(() => {
 *     setStyles.current.forEach((fn) => fn({ opacity: 0 }))
 *   }, [itemsRefs])
 *
 *   return items.map((item, i) => <div ref={itemsRefs.current[i]}>{item}</div>)
 * }
 * ```
 */
function useSetStyles(refs, setStyleParams, deps) {
    if (deps === void 0) { deps = []; }
    var setter = useRef(null);
    useIsomorphicLayoutEffect(function () {
        if (refs.current) {
            setter.current = refs.current.map(function (ref) {
                var _a;
                return gsap.quickSetter(ref.current, (_a = setStyleParams === null || setStyleParams === void 0 ? void 0 : setStyleParams.property) !== null && _a !== void 0 ? _a : 'css', setStyleParams === null || setStyleParams === void 0 ? void 0 : setStyleParams.unit);
            });
        }
        return function () {
            setter.current = null;
        };
    }, __spreadArray([setStyleParams === null || setStyleParams === void 0 ? void 0 : setStyleParams.property, setStyleParams === null || setStyleParams === void 0 ? void 0 : setStyleParams.unit], deps));
    return setter;
}

/**
 * Get a function to set styles on a single element using gsap.quickSetter
 * @param ref HTMLElement target in a React ref
 * @param setStyleParams gsap quickSetter params
 * @param deps if present, quickSetter will rebuild if the values in the list change
 *
 * @example
 * ```tsx
 * import { useRef } from 'react'
 * import { useIsomorphicLayoutEffect } from '@unlikelystudio/react-hooks'
 * import { useSetStyle } from '@unlikelystudio/react-gsap-hooks'
 *
 * const Demo = ({ items }) => {
 *   const componentRef = useRef()
 *   const setStyle = useSetStyle(componentRef)
 *
 *   useIsomorphicLayoutEffect(() => {
 *     setStyle({ opacity: 0 })
 *   }, [componentRef])
 *
 *   return <div ref={componentRef} />
 * }
 * ```
 */
function useSetStyle(ref, setStyleParams, deps) {
    var setter = useSetStyles({ current: [ref] }, setStyleParams, deps);
    var set = useCallback(function (value) {
        var _a, _b;
        (_b = (_a = setter === null || setter === void 0 ? void 0 : setter.current) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.call(_a, value);
    }, []);
    return set;
}

/**
 * Split HTML text into characters, words and lines
 * @param ref DOM element to target
 * @param opts SplitText options
 * @param deps if present, SplitText will trigger a new split if the values in the list change
 *
 * @example
 * ```tsx
 * import { useRef } from 'react'
 * import { useTimeline, useSplitText } from '@unlikelystudio/react-gsap-hooks'
 *
 * const Demo = () => {
 *   const componentRef = useRef()
 *   const splitText = useSplitText(componentRef, {
 *     type: 'words,lines'
 *   })
 *
 *   useTimeline({ repeat: -1, yoyo: true }, (tl) => {
 *     if (splitText.current?.words?.length > 0) {
 *       tl.fromTo(splitText.current.words, {
 *         opacity: 1,
 *       }, {
 *         opacity: 0,
 *          stagger: 0.15,
 *          duration: 0.3,
 *       })
 *     }
 *   }, [splitText])
 *
 *   return <div ref={componentRef}>Hello world!</div>
 * }
 * ```
 */
function useSplitText(ref, opts, deps) {
    if (deps === void 0) { deps = []; }
    var splitTextInstance = useRef(null);
    useIsomorphicLayoutEffect(function () {
        splitTextInstance.current = new SplitText(ref.current, opts);
    }, []);
    useIsomorphicLayoutEffect(function () {
        var _a;
        (_a = splitTextInstance.current) === null || _a === void 0 ? void 0 : _a.split();
        return function () {
            var _a;
            (_a = splitTextInstance.current) === null || _a === void 0 ? void 0 : _a.revert();
        };
    }, deps);
    return splitTextInstance;
}

var basic = function (current, target, params) {
  var delta = current - target;
  return current - delta / params.friction;
};

var frictionMultiplier = 100; // uniform friction through interpolation

var elastic = function (current, target, params, dTime, reset, key) {
  if (reset) this._resetSpeed(key);

  var speed = this._getSpeed(key);
  var delta = current - target;
  var spring = -delta / (params.friction / frictionMultiplier);
  var damper = -params.rigidity * speed;
  var acc = (spring + damper) / params.mass;

  speed += acc * dTime;
  this._setSpeed(speed, key);
  return current + speed * dTime;
};

var bounce = function (current, target, params, dTime, reset, key) {
  var result = elastic.call(this, current, target, params, dTime, reset, key);

  if (result - target >= 0) {
    this._setSpeed(-this._getSpeed(key), key);
    return target;
  }

  return result;
};

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) {
  return typeof obj;
} : function (obj) {
  return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
};











var classCallCheck = function (instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
};

var createClass = function () {
  function defineProperties(target, props) {
    for (var i = 0; i < props.length; i++) {
      var descriptor = props[i];
      descriptor.enumerable = descriptor.enumerable || false;
      descriptor.configurable = true;
      if ("value" in descriptor) descriptor.writable = true;
      Object.defineProperty(target, descriptor.key, descriptor);
    }
  }

  return function (Constructor, protoProps, staticProps) {
    if (protoProps) defineProperties(Constructor.prototype, protoProps);
    if (staticProps) defineProperties(Constructor, staticProps);
    return Constructor;
  };
}();

var Inrtia = function () {
  function Inrtia(_ref) {
    var value = _ref.value,
        _ref$interpolation = _ref.interpolation,
        interpolation = _ref$interpolation === undefined ? 'basic' : _ref$interpolation,
        _ref$precisionStop = _ref.precisionStop,
        precisionStop = _ref$precisionStop === undefined ? 0.001 : _ref$precisionStop,
        _ref$perfectStop = _ref.perfectStop,
        perfectStop = _ref$perfectStop === undefined ? false : _ref$perfectStop,
        _ref$rigidity = _ref.rigidity,
        rigidity = _ref$rigidity === undefined ? 0.1 : _ref$rigidity,
        _ref$friction = _ref.friction,
        friction = _ref$friction === undefined ? 10 : _ref$friction,
        _ref$mass = _ref.mass,
        mass = _ref$mass === undefined ? 0.01 : _ref$mass;
    classCallCheck(this, Inrtia);

    // Property params
    this.value = value;
    this.stopped = true;

    // Detect if value is Array of Object
    this.complex = (typeof value === 'undefined' ? 'undefined' : _typeof(value)) === 'object';

    // If complex we extract keys and we copy array / object
    if (this.complex) {
      this.keys = Object.keys(this.value);
      if (Array.isArray(value)) {
        this.targetValue = value.slice(0);
      } else {
        this.targetValue = {};
        var l = this.keys.length;
        while (l--) {
          this.targetValue[this.keys[l]] = this.value[this.keys[l]];
        }
      }
    } else {
      this.targetValue = value;
    }

    // Stop params
    this.precisionStop = precisionStop;
    this.perfectStop = perfectStop;

    // Interpolation params
    this.interpolationFn = interpolation instanceof Function ? interpolation : this.getInterpolation(interpolation);
    this.interpolationParams = { rigidity: rigidity, friction: friction, mass: mass };
  }

  createClass(Inrtia, [{
    key: 'getInterpolation',
    value: function getInterpolation(interpolationName) {
      switch (interpolationName) {
        case 'elastic':
          return elastic;
        case 'bounce':
          return bounce;
        default:
          return basic;
      }
    }
  }, {
    key: 'to',
    value: function to(targetValue) {
      if (this.complex) {
        var key = void 0,
            l = this.keys.length;

        while (l--) {
          key = this.keys[l];
          if (targetValue[key] || targetValue[key] === 0) this.targetValue[key] = targetValue[key];
        }
      } else {
        this.targetValue = targetValue;
      }

      if (this.stopped) this._start();
    }
  }, {
    key: '_start',
    value: function _start() {
      this.reset = true;
      this.stopped = false;
      this.lastTime = Date.now() - 17;
    }
  }, {
    key: 'update',
    value: function update() {
      var deltaTime = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;

      if (this.stopped) return this.value;

      var now = Date.now();
      var dTime = (deltaTime || now - this.lastTime) / 1000;
      var needStop = true;

      // Loop if object is complex
      if (this.complex) {
        var key = void 0,
            l = this.keys.length;

        while (l--) {
          key = this.keys[l];
          this.value[key] = this._updateValue(this.value[key], this.targetValue[key], dTime, key);
          needStop = needStop && this._needStop(this.value[key], this.targetValue[key], key);
        }
      } else {
        this.value = this._updateValue(this.value, this.targetValue, dTime, false);
        needStop = this._needStop(this.value, this.targetValue, false);
      }

      if (this.reset) this.reset = false;

      if (needStop) {
        if (this.perfectStop) {
          if (this.complex) {
            var key2 = void 0,
                l2 = this.keys.length;

            while (l2--) {
              key2 = this.keys[l2];
              this.value[key2] = this.targetValue[key2];
            }
          } else {
            this.value = this.targetValue;
          }
        }

        this.stop();
      }

      this.lastTime = now;
      return this.value;
    }
  }, {
    key: '_updateValue',
    value: function _updateValue(value, targetValue, dTime, key) {
      return this.interpolationFn.call(this, value, targetValue, this.interpolationParams, dTime, this.reset, key);
    }
  }, {
    key: '_needStop',
    value: function _needStop(value, targetValue, key) {
      var diff = value - targetValue;
      var speed = this._getSpeed(key);

      if (Math.abs(diff) < this.precisionStop) {
        if (isNaN(speed)) return true;
        return Math.abs(speed) < this.precisionStop;
      }return false;
    }
  }, {
    key: '_setSpeed',
    value: function _setSpeed(value, key) {
      if (this.complex) this.speed[key] = value;else this.speed = value;
    }
  }, {
    key: '_getSpeed',
    value: function _getSpeed(key) {
      if (this.complex) return this.speed && this.speed[key];
      return this.speed;
    }
  }, {
    key: '_resetSpeed',
    value: function _resetSpeed(key) {
      if (this.complex) {
        if (!this.speed) this.speed = {};
        this.speed[key] = 0;
      } else this.speed = 0;
    }
  }, {
    key: 'stop',
    value: function stop() {
      this.stopped = true;
    }
  }, {
    key: 'destroy',
    value: function destroy() {
      this.stop();
      this.interpolationFn = null;
      this.interpolationParams = null;
    }
  }]);
  return Inrtia;
}();

/**
 * Equivalent to requestAnimationFrame but using internal gsap ticker
 * @param callback function to trigger on each frame
 * @param pause if true, the callback function will not be executed
 *
 * @example
 * ```tsx
 * import { useTicker } from '@unlikelystudio/react-gsap-hooks'
 *
 * const Demo = () => {
 *   useTicker((time) => {
 *     console.log('UPDATE', time)
 *   })
 *
 *   return null
 * }
 * ```
 */
function useTicker(callback, pause) {
    useIsomorphicLayoutEffect(function () {
        if (!pause && callback) {
            gsap.ticker.add(callback);
        }
        return function () {
            gsap.ticker.remove(callback);
        };
    }, [callback, pause]);
}

var defaultConfig = {
    friction: 50,
    rigidity: 0.15,
    interpolation: 'elastic',
    perfectStop: true,
    precisionStop: 0.001,
};
var useSpring = function (_a) {
    var _b = _a.config, config = _b === void 0 ? {} : _b, onUpdate = _a.onUpdate, initialValue = __rest(_a, ["config", "onUpdate"]);
    var _c = useState(true), isRunning = _c[0], setIsRunning = _c[1];
    var inrtia = useRef(new Inrtia(__assign({ value: initialValue }, __assign(__assign({}, defaultConfig), config))));
    var prevValue = useRef(initialValue);
    var onFrame = useCallback(function (_time, dTime) {
        inrtia.current.update(Math.min(dTime, 64));
        onUpdate && onUpdate(inrtia.current.value, prevValue.current);
        prevValue.current = __assign({}, inrtia.current.value);
    }, [onUpdate]);
    useTicker(onFrame, !isRunning);
    useIsomorphicLayoutEffect(function () {
        return function () {
            inrtia.current.stop();
        };
    }, []);
    var set = useCallback(function (_a) {
        var config = _a.config, value = __rest(_a, ["config"]);
        if (config) {
            var precisionStop = config.precisionStop, perfectStop = config.perfectStop, interpolationParams = __rest(config, ["precisionStop", "perfectStop"]);
            inrtia.current.interpolationParams = __assign(__assign({}, inrtia.current.interpolationParams), interpolationParams);
            if (precisionStop)
                inrtia.current.precisionStop = precisionStop;
            if (perfectStop)
                inrtia.current.perfectStop = perfectStop;
        }
        inrtia.current.to(value);
    }, []);
    var stop = useCallback(function () { return setIsRunning(false); }, []);
    var start = useCallback(function () { return setIsRunning(true); }, []);
    return [set, stop, start, inrtia];
};

/* eslint-disable no-undefined,no-param-reassign,no-shadow */

/**
 * Throttle execution of a function. Especially useful for rate limiting
 * execution of handlers on events like resize and scroll.
 *
 * @param  {number}    delay -          A zero-or-greater delay in milliseconds. For event callbacks, values around 100 or 250 (or even higher) are most useful.
 * @param  {boolean}   [noTrailing] -   Optional, defaults to false. If noTrailing is true, callback will only execute every `delay` milliseconds while the
 *                                    throttled-function is being called. If noTrailing is false or unspecified, callback will be executed one final time
 *                                    after the last throttled-function call. (After the throttled-function has not been called for `delay` milliseconds,
 *                                    the internal counter is reset).
 * @param  {Function}  callback -       A function to be executed after delay milliseconds. The `this` context and all arguments are passed through, as-is,
 *                                    to `callback` when the throttled-function is executed.
 * @param  {boolean}   [debounceMode] - If `debounceMode` is true (at begin), schedule `clear` to execute after `delay` ms. If `debounceMode` is false (at end),
 *                                    schedule `callback` to execute after `delay` ms.
 *
 * @returns {Function}  A new, throttled, function.
 */
function throttle (delay, noTrailing, callback, debounceMode) {
  /*
   * After wrapper has stopped being called, this timeout ensures that
   * `callback` is executed at the proper times in `throttle` and `end`
   * debounce modes.
   */
  var timeoutID;
  var cancelled = false; // Keep track of the last time `callback` was executed.

  var lastExec = 0; // Function to clear existing timeout

  function clearExistingTimeout() {
    if (timeoutID) {
      clearTimeout(timeoutID);
    }
  } // Function to cancel next exec


  function cancel() {
    clearExistingTimeout();
    cancelled = true;
  } // `noTrailing` defaults to falsy.


  if (typeof noTrailing !== 'boolean') {
    debounceMode = callback;
    callback = noTrailing;
    noTrailing = undefined;
  }
  /*
   * The `wrapper` function encapsulates all of the throttling / debouncing
   * functionality and when executed will limit the rate at which `callback`
   * is executed.
   */


  function wrapper() {
    for (var _len = arguments.length, arguments_ = new Array(_len), _key = 0; _key < _len; _key++) {
      arguments_[_key] = arguments[_key];
    }

    var self = this;
    var elapsed = Date.now() - lastExec;

    if (cancelled) {
      return;
    } // Execute `callback` and update the `lastExec` timestamp.


    function exec() {
      lastExec = Date.now();
      callback.apply(self, arguments_);
    }
    /*
     * If `debounceMode` is true (at begin) this is used to clear the flag
     * to allow future `callback` executions.
     */


    function clear() {
      timeoutID = undefined;
    }

    if (debounceMode && !timeoutID) {
      /*
       * Since `wrapper` is being called for the first time and
       * `debounceMode` is true (at begin), execute `callback`.
       */
      exec();
    }

    clearExistingTimeout();

    if (debounceMode === undefined && elapsed > delay) {
      /*
       * In throttle mode, if `delay` time has been exceeded, execute
       * `callback`.
       */
      exec();
    } else if (noTrailing !== true) {
      /*
       * In trailing throttle mode, since `delay` time has not been
       * exceeded, schedule `callback` to execute `delay` ms after most
       * recent execution.
       *
       * If `debounceMode` is true (at begin), schedule `clear` to execute
       * after `delay` ms.
       *
       * If `debounceMode` is false (at end), schedule `callback` to
       * execute after `delay` ms.
       */
      timeoutID = setTimeout(debounceMode ? clear : exec, debounceMode === undefined ? delay - elapsed : delay);
    }
  }

  wrapper.cancel = cancel; // Return the wrapper function.

  return wrapper;
}

/**
 * Callback debouncing
 * @param callback debounced function to call
 * @param delay delay in milliseconds
 * @param params throttle-debounce options
 * @param params.debounceMode
 * @param params.noTrailing
 * @param deps throttled callback dependencies
 *
 * @example
 * ```ts
 * import { useThrottleCallback } from '@unlikelystudio/react-hooks'
 *
 * const Demo = () => {
 *   useThrottleCallback(() => {
 *     console.log('DEBOUNCED FUNCTION')
 *   }, 250, { noTrailing: true }, [])
 *
 *   return null
 * }
 * ```
 */
function useThrottleCallback(callback, delay, params, deps) {
    var callbackDeps = __spreadArray([
        delay,
        params === null || params === void 0 ? void 0 : params.debounceMode,
        params === null || params === void 0 ? void 0 : params.noTrailing
    ], deps);
    return useCallback(function () {
        return throttle(delay, params === null || params === void 0 ? void 0 : params.noTrailing, callback, params === null || params === void 0 ? void 0 : params.debounceMode);
    }, callbackDeps);
}

/**
 * Generates a new gsap timeline
 * @param options passed options to gsap.timeline
 * @param buildTl function used to build the timeline
 * @param deps if present, timeline will rebuild if the values in the list change
 *
 * @example
 * ```tsx
 * import { useRef } from 'react'
 * import { useTimeline } from '@unlikelystudio/react-gsap-hooks'
 *
 * const Demo = () => {
 *   const componentRef = useRef()
 *   const componentTl = useTimeline({ paused: true }, (tl) => {
 *     tl.fromTo(
 *       componentRef.current,
 *       {
 *         opacity: 0,
 *       },
 *       {
 *         opacity: 1,
 *       },
 *     )
 *   })
 *
 *   return <div ref={componentRef} onClick={() => componentTl.play()} />
 * }
 * ```
 */
function useTimeline(_a, buildTl, deps) {
    if (deps === void 0) { deps = []; }
    var restoreTime = _a.restoreTime, config = __rest(_a, ["restoreTime"]);
    var tl = useRef(gsap.timeline(config));
    var prevTime = useRef(null);
    useIsomorphicLayoutEffect(function () {
        buildTl(tl.current);
        if (restoreTime && prevTime.current) {
            tl.current.seek(prevTime.current);
        }
        return function () {
            prevTime.current = tl.current.time();
            tl.current.clear();
        };
    }, deps);
    useIsomorphicLayoutEffect(function () {
        return function () {
            tl.current.kill();
        };
    }, []);
    return tl.current;
}

/**
 * Play or reverse a timeline depending on a playback parameter
 * @param tl gsap timeline object
 * @param playback if true, timeline will play and if false, timeline will reverse
 *
 * @example
 * ```tsx
 * import { useRef } from 'react'
 * import { useTimeline, useTimelinePlayback } from '@unlikelystudio/react-gsap-hooks'
 *
 * const Demo = ({ value }) => {
 *   const [state, setState] = useState(false)
 *   const componentRef = useRef()
 *
 *   const componentTl = useTimeline({ paused: true }, (tl) => {
 *     tl.fromTo(
 *       componentRef.current,
 *       {
 *         opacity: 0,
 *       },
 *       {
 *         opacity: 1,
 *       },
 *     )
 *   })
 *
 *   useTimelinePlayback(componentTl, state)
 *
 *   return <div ref={componentRef} onClick={() => setState(s => !s)} />
 * }
 * ```
 */
function useTimelinePlayback(tl, playback) {
    useIsomorphicLayoutEffect(function () {
        if (tl) {
            playback ? tl.play() : tl.reverse();
        }
    }, [playback]);
}

/**
 * Generates a new gsap tween
 * @param buildTween function used to build the tween
 * @param deps if present, tween will rebuild if the values in the list change
 *
 * @example
 * ```tsx
 * import { useRef } from 'react'
 * import { gsap } from 'gsap'
 * import { useTween } from '@unlikelystudio/react-gsap-hooks'
 *
 * const Demo = ({ value }) => {
 *   const componentRef = useRef()
 *   const tween = useTween(() => {
 *     gsap.to(componentRef, {
 *       opacity: 0,
 *       duration: .4
 *     })
 *   }, [])
 *
 *   return <div ref={componentRef} onClick={() => tween.play()} />
 * }
 * ```
 */
function useTween(buildTween, deps) {
    if (deps === void 0) { deps = []; }
    var tween = useRef();
    useIsomorphicLayoutEffect(function () {
        tween.current = buildTween();
        return function () {
            tween.current && tween.current.kill();
        };
    }, deps);
    return tween.current;
}

function lerp(start, end, amt) {
    return (1 - amt) * start + amt * end;
}

var PRECISION = .0001;
/**
 * Animate a gsap timeline based on the position of a DOM element in viewport
 * @param el React ref object of a timeline
 * @param config object containing the configuration for your timeline
 * @param config.easing the easing applied to the timeline animation. Value between 0 and 1
 * @param config.offset the offset from which to start the animation. Value is specified in viewports (eg. 2 = 2 viewports offset)
 * @param config.endOffset the endOffset from which to end the animation. Value is specified in viewports (eg. 2 = 2 viewports offset)
 *
 * @example
 * ```tsx
 * import { useRef, useLayoutEffect } from 'react'
 * import { gsap } from 'gsap'
 * import { useElevator } from '@unlikelystudio/react-hooks'
 *
 * const Demo = ({ value }) => {
 *   const animatedComponent = useRef()
 *   const [tl] = useElevator(componentRef, { easing: 0.1, offset: 1, endOffset: 1 })
 *
 *   useLayoutEffect(() => {
 *     tl.fromTo()
 *     ...
 *   }, [])
 *
 *   return <div ref={animatedComponent} />
 * }
 * ```
 */
function useElevator(el, _a) {
    var easing = _a.easing, offset = _a.offset, endOffset = _a.endOffset;
    var _b = useState(true), paused = _b[0], setTickerPauseState = _b[1];
    var perc = useRef(0);
    var offsetY = useRef(0);
    var h = useRef(0);
    var viewportH = useRef(0);
    var tl = useRef(gsap.timeline({ paused: true }));
    var getScrollTop = useCallback(function () {
        return window.pageYOffset || document.documentElement.scrollTop;
    }, []);
    var raf = useCallback(function () {
        var scrollY = getScrollTop();
        var targetPerc = clamp((scrollY - offsetY.current + viewportH.current) /
            (viewportH.current + h.current), 0, 1);
        perc.current = lerp(perc.current, targetPerc, easing);
        perc.current = Math.round(perc.current * 100000) / 100000;
        if (perc.current > 1 - PRECISION || perc.current < PRECISION)
            return;
        tl.current.progress(perc.current);
    }, []);
    var compute = useCallback(function () {
        var rect = el.current.getBoundingClientRect();
        var viewportH = window.innerHeight;
        offsetY.current = rect.top + getScrollTop() - offset * viewportH;
        h.current = rect.height + offset * viewportH - endOffset * viewportH;
    }, []);
    useTicker(raf, paused);
    useIsomorphicLayoutEffect(function () {
        compute();
        setTickerPauseState(false); // Start ticker loop
    }, []);
    return [tl];
}

export { clamp, lerp, useDrag, useElevator, useIsHover, useIsMounted, useIsMountedState, useIsTouchScreen, useIsomorphicLayoutEffect, useLatest, useLatestCallback, useMeasure, useMeasureObserver, useOnResize, useRaf, useRefs, useSetStyle, useSetStyles, useSplitText, useSpring, useThrottleCallback, useTicker, useTimeline, useTimelinePlayback, useTween, useWindowSize };
//# sourceMappingURL=index.es.js.map
